import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus, Trash2 } from "lucide-react";
import {
  adminAdminUsersQuery,
  adminAddAdminUser,
  adminRemoveAdminUser,
  type AdminUserRow,
} from "@/lib/admin/admin-users.functions";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/admin/usuarios")({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const queryClient = useQueryClient();
  const currentUser = useAuth((s) => s.user);
  const { data: admins, isLoading } = useQuery(adminAdminUsersQuery());

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await adminAddAdminUser({ data: { email } });
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["admin", "admin-users"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível promover esse usuário.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (admin: AdminUserRow) => {
    const isSelf = admin.user_id === currentUser?.id;
    const confirmMessage = isSelf
      ? "Você está removendo o SEU PRÓPRIO acesso de administrador e vai perder acesso a este painel imediatamente. Confirmar?"
      : `Remover o acesso de administrador de ${admin.email ?? admin.user_id}?`;
    if (!window.confirm(confirmMessage)) return;

    setError("");
    setRemovingId(admin.user_id);
    try {
      await adminRemoveAdminUser({ data: admin.user_id });
      queryClient.invalidateQueries({ queryKey: ["admin", "admin-users"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível remover esse administrador.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">
        Usuários administradores
      </h1>

      <form
        onSubmit={handleAdd}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-white p-4"
      >
        <div className="min-w-[240px] flex-1">
          <label
            className="mb-1 block text-xs font-semibold uppercase text-muted-foreground"
            htmlFor="admin-email"
          >
            E-mail do usuário
          </label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@exemplo.com"
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-md bg-eg-gold px-4 py-2 text-sm font-semibold text-eg-dark transition-colors hover:bg-eg-goldhover disabled:opacity-60"
        >
          <UserPlus className="h-4 w-4" />
          {submitting ? "Promovendo..." : "Promover a admin"}
        </button>
      </form>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Admin desde</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {admins?.map((admin) => (
              <tr key={admin.user_id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-eg-dark">
                  {admin.email ?? (
                    <span className="italic text-muted-foreground">e-mail indisponível</span>
                  )}
                  {admin.user_id === currentUser?.id && (
                    <span className="ml-2 rounded-full bg-eg-gold/20 px-2 py-0.5 text-xs font-semibold text-eg-goldtext">
                      você
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {new Date(admin.created_at).toLocaleDateString("pt-BR")}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => handleRemove(admin)}
                    disabled={removingId === admin.user_id}
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-60"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && admins?.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Nenhum administrador cadastrado.
          </p>
        )}
      </div>
    </div>
  );
}
